import type { RootState } from '@reducers/index';
import {
  RadiusSearch,
  setRadiusDialogStatus,
  updateGeo,
  updateRadiusSearch,
} from '@reducers/search';
import { setPreventPopstate } from '@reducers/system';
import GeoSearch, { GeoSearchSelection } from '@sbt-web/geo-search';
import '@sbt-web/geo-search/dist/index.css';
import { useViewport } from '@sbt-web/hooks';
import { CategoryID, FilterURI, GeoEntry } from '@sbt-web/network/types';
import { MOBILE_API_CHANNEL, WEB_API_CHANNEL } from '@shared/constants';
import { getOrCreatePulse } from '@tools/tracking/utils';
import React, { useCallback, useMemo } from 'react';
import { batch, connect } from 'react-redux';

interface OwnProps {
  geoLimit?: number;
}

interface StateProps {
  geo: GeoEntry;
  searchNearRegions?: boolean;
  radiusSearch?: RadiusSearch;
  totalItems: number;
  isImmobili: boolean;
}

interface DispatchProps {
  updateGeo: (geo: GeoEntry, snr?: boolean) => void;
  updateRadiusPreview: (radiusSearch: RadiusSearch) => void;
  updateRadiusSearch: (radiusSearch: RadiusSearch) => void;
  updateRadiusDialogStatus: (isOpen: boolean) => void;
}

type Props = OwnProps & StateProps & DispatchProps;

export function QueryGeoComponent({
  geo,
  searchNearRegions,
  geoLimit,
  updateGeo,
  updateRadiusPreview,
  updateRadiusSearch,
  updateRadiusDialogStatus,
  radiusSearch,
  totalItems,
  isImmobili,
}: Props) {
  const { isMobile } = useViewport();

  const isRadiusEnabled = isMobile && isImmobili;

  const signalDialogOpening = useCallback(() => {
    /**
     * We need to disable popping the state because otherwise,
     * when the dialog opens/closes and the location hash changes,
     * the listing page will try to reconciliate the url with the
     * redux state. The hash change happens before the location change
     * so the previous state will be re-set.
     */
    updateRadiusDialogStatus(true);
  }, [updateRadiusDialogStatus]);

  const startingValue = useMemo<GeoSearchSelection>(() => {
    if (radiusSearch != null) {
      return {
        entry: {
          point: radiusSearch.center,
          friendlyName: radiusSearch.friendlyName,
          addressToTrack: radiusSearch.addressToTrack,
        },
        radius: radiusSearch.radiusMeters,
        responseFrom: 'here',
      };
    } else {
      return {
        entry: geo,
        searchNearRegions: searchNearRegions ?? false,
        responseFrom: 'geogos',
      };
    }
  }, [geo, radiusSearch, searchNearRegions]);

  const updateResultsPreview = useCallback(
    (selection: GeoSearchSelection) => {
      if (selection.responseFrom === 'geogos') {
        return;
      } else if (selection.responseFrom === 'here') {
        updateRadiusPreview({
          radiusMeters: selection.radius,
          center: selection.entry.point,
          friendlyName: selection.entry.friendlyName,
        });
      }
    },
    [updateRadiusPreview]
  );

  const handleSelection = useCallback(
    (selection: GeoSearchSelection) => {
      if (selection.responseFrom === 'geogos') {
        updateGeo(selection.entry, selection.searchNearRegions);
      } else if (selection.responseFrom === 'here') {
        updateRadiusSearch({
          radiusMeters: selection.radius,
          center: selection.entry.point,
          addressToTrack: selection.entry.addressToTrack,
          friendlyName: selection.entry.friendlyName,
        });
      }
    },
    [updateGeo, updateRadiusSearch]
  );

  return (
    <GeoSearch
      variant={isRadiusEnabled ? 'radius' : 'base'}
      hereApiKey={process.env.NEXT_PUBLIC_HERE_WEB_KEY}
      assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL}
      channel={isMobile ? MOBILE_API_CHANNEL : WEB_API_CHANNEL}
      hadesBasePath={process.env.NEXT_PUBLIC_HADES_BASE_URL}
      value={startingValue}
      name={FilterURI.Geo}
      onDialogOpen={signalDialogOpening}
      onSelect={handleSelection}
      resultsLimit={geoLimit}
      pulseInstance={getOrCreatePulse()}
      resultsPreview={totalItems}
      updateResultsPreview={updateResultsPreview}
    />
  );
}

const immobiliCategoryIds: CategoryID[] = [
  CategoryID.Immobili,
  CategoryID.Appartamenti,
  CategoryID.CamerePostiLetto,
  CategoryID.Ville,
  CategoryID.TerreniRustici,
  CategoryID.GarageBox,
  CategoryID.LoftMansarde,
  CategoryID.CasaPersona,
  CategoryID.UfficiLocali,
];

export const QueryGeoInput = connect<
  StateProps,
  DispatchProps,
  unknown,
  RootState
>(
  (state) => ({
    geo: state.search.geo,
    searchNearRegions: state.search.searchNearRegions,
    radiusSearch: state.search.radiusSearch as RadiusSearch | undefined,
    totalItems: state.items.total,
    isImmobili: immobiliCategoryIds.includes(state.search.category.id),
  }),
  (dispatch) => ({
    updateGeo: (geo: GeoEntry, snr?: boolean) => {
      batch(() => {
        dispatch(setPreventPopstate(false));
        dispatch(setRadiusDialogStatus(false));
        dispatch(updateGeo(geo, snr));
      });
    },
    updateRadiusPreview: (radiusSearch: RadiusSearch) => {
      dispatch(updateRadiusSearch({ radiusSearch, isFinalSearch: false }));
    },
    updateRadiusSearch: (radiusSearch: RadiusSearch) => {
      batch(() => {
        dispatch(setPreventPopstate(false));
        dispatch(setRadiusDialogStatus(false));
        dispatch(updateRadiusSearch({ radiusSearch, isFinalSearch: true }));
      });
    },
    updateRadiusDialogStatus: (isOpen: boolean) => {
      batch(() => {
        dispatch(setPreventPopstate(isOpen));
        dispatch(setRadiusDialogStatus(isOpen));
      });
    },
  })
)(QueryGeoComponent);
